import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { FileText, Plus, Trash2, Edit2, Hash, Search, Clock, Save } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const COMMON_TAGS = [
  '#transference',
  '#countertransference',
  '#homework-idea',
  '#referral-candidate',
  '#burnout-risk',
  '#breakthrough',
  '#resistance',
  '#attachment',
  '#supervision',
  '#boundary-issue'
];

const PrivateNotes = ({ clientId, clientName }) => {
  const [scratchpad, setScratchpad] = useState('');
  const [scratchpadId, setScratchpadId] = useState(null);
  const [notebookEntries, setNotebookEntries] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [newEntryTitle, setNewEntryTitle] = useState('');
  const [newEntryContent, setNewEntryContent] = useState('');
  const [newEntryTags, setNewEntryTags] = useState('');
  const [showNewEntryForm, setShowNewEntryForm] = useState(false);

  const saveTimeoutRef = useRef(null);
  const scratchpadRef = useRef(null);

  // Fetch all private notes on load
  useEffect(() => {
    if (clientId) {
      fetchPrivateNotes();
    }
  }, [clientId]);

  // Auto-save scratchpad with debouncing
  useEffect(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    if (scratchpad !== null && scratchpadId !== null) {
      saveTimeoutRef.current = setTimeout(() => {
        saveScratchpad();
      }, 1000); // Save after 1 second of no typing
    }

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [scratchpad]);

  // Filter notebook entries based on search and tags
  useEffect(() => {
    let filtered = [...notebookEntries];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(entry =>
        entry.title?.toLowerCase().includes(query) ||
        entry.content?.toLowerCase().includes(query) ||
        entry.tags?.toLowerCase().includes(query)
      );
    }

    if (selectedTag) {
      filtered = filtered.filter(entry =>
        entry.tags?.includes(selectedTag)
      );
    }

    setFilteredEntries(filtered);
  }, [notebookEntries, searchQuery, selectedTag]);

  const fetchPrivateNotes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/clients/${clientId}/private-notes`);
      const notes = response.data || [];

      // Separate scratchpad from notebook entries
      const scratchpadNote = notes.find(n => n.note_type === 'scratchpad');
      const notebookNotes = notes.filter(n => n.note_type === 'notebook');

      if (scratchpadNote) {
        setScratchpad(scratchpadNote.content || '');
        setScratchpadId(scratchpadNote.id);
        setLastSaved(new Date(scratchpadNote.updated_at));
      } else {
        // Create initial scratchpad
        createInitialScratchpad();
      }

      setNotebookEntries(notebookNotes);
    } catch (error) {
      console.error('Error fetching private notes:', error);
      toast.error('Failed to load private notes');
    } finally {
      setLoading(false);
    }
  };

  const createInitialScratchpad = async () => {
    try {
      const response = await axios.post(`${API_URL}/clients/${clientId}/private-notes`, {
        content: '',
        note_type: 'scratchpad'
      });
      setScratchpadId(response.data.id);
      setLastSaved(new Date(response.data.created_at));
    } catch (error) {
      console.error('Error creating scratchpad:', error);
    }
  };

  const saveScratchpad = async () => {
    if (!scratchpadId) return;

    try {
      setIsSaving(true);
      await axios.put(`${API_URL}/private-notes/${scratchpadId}`, {
        content: scratchpad
      });
      setLastSaved(new Date());
    } catch (error) {
      console.error('Error saving scratchpad:', error);
      toast.error('Failed to save scratchpad');
    } finally {
      setIsSaving(false);
    }
  };

  const createNotebookEntry = async () => {
    if (!newEntryContent.trim()) {
      toast.error('Content is required');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/clients/${clientId}/private-notes`, {
        content: newEntryContent,
        title: newEntryTitle || null,
        tags: newEntryTags || null,
        note_type: 'notebook'
      });

      setNotebookEntries(prev => [response.data, ...prev]);
      setNewEntryTitle('');
      setNewEntryContent('');
      setNewEntryTags('');
      setShowNewEntryForm(false);
      toast.success('Note created');
    } catch (error) {
      console.error('Error creating notebook entry:', error);
      toast.error('Failed to create note');
    }
  };

  const updateNotebookEntry = async (id) => {
    if (!editingEntry) return;

    try {
      const response = await axios.put(`${API_URL}/private-notes/${id}`, {
        content: editingEntry.content,
        title: editingEntry.title,
        tags: editingEntry.tags
      });

      setNotebookEntries(prev =>
        prev.map(entry => entry.id === id ? response.data : entry)
      );
      setEditingEntry(null);
      toast.success('Note updated');
    } catch (error) {
      console.error('Error updating notebook entry:', error);
      toast.error('Failed to update note');
    }
  };

  const deleteNotebookEntry = async (id) => {
    if (!window.confirm('Are you sure you want to delete this note?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/private-notes/${id}`);
      setNotebookEntries(prev => prev.filter(entry => entry.id !== id));
      toast.success('Note deleted');
    } catch (error) {
      console.error('Error deleting notebook entry:', error);
      toast.error('Failed to delete note');
    }
  };

  const extractTagsFromText = (text) => {
    const tagRegex = /#[\w-]+/g;
    const matches = text.match(tagRegex);
    return matches ? [...new Set(matches)] : [];
  };

  const allTags = [
    ...COMMON_TAGS,
    ...notebookEntries.flatMap(entry => extractTagsFromText(entry.tags || '')),
    ...extractTagsFromText(scratchpad)
  ].filter((tag, index, self) => self.indexOf(tag) === index);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4" style={{ borderColor: 'var(--border-color)', borderTopColor: 'var(--accent)' }}></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
            Private Coach Notes
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
            For {clientName} - Never visible to client
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-tertiary)' }}>
          {isSaving ? (
            <>
              <Save className="w-4 h-4 animate-pulse" />
              <span>Saving...</span>
            </>
          ) : lastSaved ? (
            <>
              <Clock className="w-4 h-4" />
              <span>Last saved {lastSaved.toLocaleTimeString()}</span>
            </>
          ) : null}
        </div>
      </div>

      {/* Main Scratchpad */}
      <div className="bg-[var(--bg-secondary)] rounded-xl border border-[var(--border-color)] p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <FileText className="w-5 h-5" />
            Quick Scratchpad
          </h2>
          <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
            Auto-saves every keystroke
          </span>
        </div>
        <textarea
          ref={scratchpadRef}
          value={scratchpad}
          onChange={(e) => setScratchpad(e.target.value)}
          placeholder="Quick notes, supervision thoughts, counter-transference reactions, hypotheses...&#10;&#10;Use #tags to organize: #transference #homework-idea #burnout-risk"
          className="w-full h-96 px-4 py-3 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[var(--accent)] font-mono text-sm leading-relaxed"
          style={{
            backgroundColor: 'var(--bg-tertiary)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-color)'
          }}
        />
      </div>

      {/* Tags Filter */}
      <div className="bg-[var(--bg-secondary)] rounded-xl border border-[var(--border-color)] p-4">
        <div className="flex items-center gap-2 mb-3">
          <Hash className="w-4 h-4" style={{ color: 'var(--text-secondary)' }} />
          <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
            Quick Tags
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {COMMON_TAGS.map(tag => (
            <button
              key={tag}
              onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                selectedTag === tag
                  ? 'bg-[var(--accent)] text-white'
                  : 'bg-[var(--bg-tertiary)] text-[var(--text-secondary)] hover:bg-[var(--accent)]/20 hover:text-[var(--accent)]'
              }`}
            >
              {tag}
            </button>
          ))}
        </div>
      </div>

      {/* Notebook Section */}
      <div className="bg-[var(--bg-secondary)] rounded-xl border border-[var(--border-color)] p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
            Organized Notebook
          </h2>
          <button
            onClick={() => setShowNewEntryForm(!showNewEntryForm)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)]"
          >
            <Plus className="w-4 h-4" />
            New Note
          </button>
        </div>

        {/* Search */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search notes by title, content, or tags..."
              className="w-full pl-10 pr-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
              style={{
                backgroundColor: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-color)'
              }}
            />
          </div>
        </div>

        {/* New Entry Form */}
        {showNewEntryForm && (
          <div className="mb-6 p-4 rounded-lg bg-[var(--bg-tertiary)] border border-[var(--border-color)]">
            <input
              type="text"
              value={newEntryTitle}
              onChange={(e) => setNewEntryTitle(e.target.value)}
              placeholder="Title (optional)"
              className="w-full px-3 py-2 mb-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
              style={{
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-color)'
              }}
            />
            <textarea
              value={newEntryContent}
              onChange={(e) => setNewEntryContent(e.target.value)}
              placeholder="Note content..."
              className="w-full px-3 py-2 mb-3 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[var(--accent)] h-32"
              style={{
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-color)'
              }}
            />
            <input
              type="text"
              value={newEntryTags}
              onChange={(e) => setNewEntryTags(e.target.value)}
              placeholder="Tags: #transference #homework-idea"
              className="w-full px-3 py-2 mb-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
              style={{
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-color)'
              }}
            />
            <div className="flex gap-2">
              <button
                onClick={createNotebookEntry}
                className="px-4 py-2 rounded-lg font-medium transition-all bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)]"
              >
                Create Note
              </button>
              <button
                onClick={() => {
                  setShowNewEntryForm(false);
                  setNewEntryTitle('');
                  setNewEntryContent('');
                  setNewEntryTags('');
                }}
                className="px-4 py-2 rounded-lg font-medium transition-all text-[var(--text-secondary)] hover:bg-[var(--bg-primary)]"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Notebook Entries */}
        <div className="space-y-3">
          {filteredEntries.length === 0 ? (
            <div className="text-center py-12" style={{ color: 'var(--text-tertiary)' }}>
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No notes yet. Create your first organized note.</p>
            </div>
          ) : (
            filteredEntries.map(entry => (
              <div
                key={entry.id}
                className="p-4 rounded-lg bg-[var(--bg-tertiary)] border border-[var(--border-color)]"
              >
                {editingEntry?.id === entry.id ? (
                  <div>
                    <input
                      type="text"
                      value={editingEntry.title || ''}
                      onChange={(e) => setEditingEntry({ ...editingEntry, title: e.target.value })}
                      placeholder="Title (optional)"
                      className="w-full px-3 py-2 mb-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
                      style={{
                        backgroundColor: 'var(--bg-primary)',
                        color: 'var(--text-primary)',
                        border: '1px solid var(--border-color)'
                      }}
                    />
                    <textarea
                      value={editingEntry.content}
                      onChange={(e) => setEditingEntry({ ...editingEntry, content: e.target.value })}
                      className="w-full px-3 py-2 mb-3 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[var(--accent)] h-32"
                      style={{
                        backgroundColor: 'var(--bg-primary)',
                        color: 'var(--text-primary)',
                        border: '1px solid var(--border-color)'
                      }}
                    />
                    <input
                      type="text"
                      value={editingEntry.tags || ''}
                      onChange={(e) => setEditingEntry({ ...editingEntry, tags: e.target.value })}
                      placeholder="Tags"
                      className="w-full px-3 py-2 mb-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
                      style={{
                        backgroundColor: 'var(--bg-primary)',
                        color: 'var(--text-primary)',
                        border: '1px solid var(--border-color)'
                      }}
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => updateNotebookEntry(entry.id)}
                        className="px-4 py-2 rounded-lg font-medium transition-all bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)]"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingEntry(null)}
                        className="px-4 py-2 rounded-lg font-medium transition-all text-[var(--text-secondary)] hover:bg-[var(--bg-primary)]"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-start justify-between mb-2">
                      {entry.title && (
                        <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                          {entry.title}
                        </h3>
                      )}
                      <div className="flex gap-2">
                        <button
                          onClick={() => setEditingEntry(entry)}
                          className="p-1.5 rounded hover:bg-[var(--bg-secondary)] transition-colors"
                          style={{ color: 'var(--text-tertiary)' }}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteNotebookEntry(entry.id)}
                          className="p-1.5 rounded hover:bg-red-500/10 transition-colors text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <p className="text-sm mb-2 whitespace-pre-wrap" style={{ color: 'var(--text-secondary)' }}>
                      {entry.content}
                    </p>
                    {entry.tags && (
                      <div className="flex flex-wrap gap-1">
                        {extractTagsFromText(entry.tags).map((tag, idx) => (
                          <span
                            key={idx}
                            onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
                            className="px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--accent)]/20 cursor-pointer hover:bg-[var(--accent)]/30 transition-colors"
                            style={{ color: 'var(--accent)' }}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="text-xs mt-2" style={{ color: 'var(--text-tertiary)' }}>
                      {new Date(entry.created_at).toLocaleString()}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default PrivateNotes;
